/* THE IN-LINE COST SHEET LIBRARY — Max, 2026-09-22: "ko làm cho nó 1 view riêng luôn để còn lưu
   trữ sau này dùng lại á".

   A screen of its own rather than a panel inside the calculator, for the same reason Pricing
   Approval is its own screen: keeping a vendor's cost sheets is not the same job as pricing a
   quote. Somebody tidies the library, names the seasons and drops what a vendor has superseded;
   nobody does that with a quote open in front of them.

   It reads the same two endpoints the calculator's picker reads, so a line shown here and a line
   offered there cannot disagree — the fold happens once, on the way in. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import Header from './Header';
import ModuleTabs from './ModuleTabs';
import ModuleHelp from './ModuleHelp';
import { BACKEND_URL } from './backendConfig';
import './PricingCalculator.css';
import './BenchmarkLibrary.css';

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

/* THE GUIDE (Max 01/11: every module has one). The card step is skipped by the tour while the library is empty. */
const TOUR = [
  { selector: '.bl-page .sp-title', title: 'tourCostSheets.introT', body: 'tourCostSheets.introB' },
  { selector: '.bl-bar .bl-search', title: 'tourCostSheets.searchT', body: 'tourCostSheets.searchB' },
  { selector: '.bl-bar .sp-btn', title: 'tourCostSheets.importT', body: 'tourCostSheets.importB' },
  { selector: '.bl-list .bl-cardhead', title: 'tourCostSheets.cardT', body: 'tourCostSheets.cardB' },
];

export default function BenchmarkLibrary() {
  const { t } = useTranslation();
  const [sheets, setSheets] = useState([]);
  const [openId, setOpenId] = useState(0);
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [confirmDel, setConfirmDel] = useState(null);
  const [edit, setEdit] = useState(null);
  const fileRef = useRef(null);
  // What the last read produced. The refused lines are as much the point as the accepted ones.
  const [read, setRead] = useState(null);

  const load = async () => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/pricing/bench-sheets`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || i18n.t('xPricing.loadLibraryFailed'));
      setSheets(data.sheets || []);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  /* THE SHEET ARRIVES HERE — Max, 2026-09-22: "import tại màn hình này luôn chứ ko phải từ Collab
     Pricing Calculator". One door in and one door out: a vendor workbook is filed on this screen,
     and the calculator only ever picks a line that is already in the library. Two ways in meant
     the calculator quietly did the filing as a side effect of a read, which is not where anybody
     would look for it afterwards.

     The server does the fold and the control; this side sends the file and reports what came
     back, including the lines it REFUSED — a sheet that loses three of its rows to a column that
     no longer adds up must say so, or it looks like a short file. */
  const importSheet = async (file) => {
    setBusy(true); setError(''); setStatus(''); setRead(null);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onerror = () => reject(new Error(t('xPricing.readFileFailed')));
        fr.onload = () => resolve(String(fr.result).split(',')[1] || '');
        fr.readAsDataURL(file);
      });
      const res = await fetch(`${BACKEND_URL}/api/pricing/import-benchmark-xlsx`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileBase64: b64, fileName: file.name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xPricing.importFailed', { status: res.status }));
      setRead({ fileName: file.name, lines: (data.rows || []).length, refused: data.refused || [] });
      await load();
      // Straight into what was just read: the person is about to name its season and vendor.
      if (data.sheetId) await openSheet(data.sheetId);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  };

  const openSheet = async (id) => {
    if (openId === id) { setOpenId(0); setDetail(null); return; }
    setOpenId(id); setDetail(null); setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/pricing/bench-sheets/${id}`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xPricing.readSheetFailed'));
      setDetail(data);
    } catch (e) { setError(e.message); }
  };

  const saveEdit = async () => {
    if (!edit) return;
    setBusy(true); setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/pricing/bench-sheets/${edit.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vendor: edit.vendor, season: edit.season, note: edit.note }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xPricing.saveFailedPlain'));
      setEdit(null); setStatus(t('bench.saved')); await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const removeSheet = async () => {
    if (!confirmDel) return;
    setBusy(true); setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/pricing/bench-sheets/${confirmDel.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xPricing.removeSheetFailed'));
      setConfirmDel(null);
      if (openId === confirmDel.id) { setOpenId(0); setDetail(null); }
      setStatus(t('bench.removed')); await load();
    } catch (e) { setError(e.message); setConfirmDel(null); } finally { setBusy(false); }
  };

  /* Searched HERE and not on the server: the list is one request and already in the page, so a
     filter that went back to the database would make typing slower than reading.

     EVERY WORD HAS TO HIT, in any order — the same rule the server applies to the calculator's
     picker. Matched as one string, "S27 Chair One" found nothing, and two search boxes over the
     same data behaving differently is worse than either behaviour on its own. */
  const shown = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return sheets;
    return sheets.filter((s) => {
      const hay = [s.fileName, s.vendor, s.season, s.style, s.note, s.createdBy]
        .filter(Boolean).join(' ').toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [sheets, q]);

  const lines = useMemo(() => (detail?.rows || []), [detail]);

  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main bl-page">
        <ModuleTabs module="pricing" />
        {/* The same title block the calculator and Pricing Approval carry — a plain h2 and one
            line. Three screens of one module that introduce themselves three ways read as three
            products. */}
        <div className="mh-titlerow">
          <div className="sp-title">
            <h2>{t('bench.title')}</h2>
            <p>{t('bench.sub')}</p>
          </div>
          <ModuleHelp steps={TOUR} tourKey="inlineCostSheets" ready={!busy} />
        </div>

        <div className="bl-bar">
          <input
            type="search"
            className="cp-benchq bl-search"
            placeholder={t('bench.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button type="button" className="sp-btn sp-btn-primary" disabled={busy}
            onClick={() => fileRef.current?.click()}>
            {t('bench.import')}
          </button>
          <input
            ref={fileRef}
            id="bl-import"
            type="file"
            accept=".xlsx"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importSheet(f); }}
          />
        </div>

      {error && <div className="sp-alert err">{error}</div>}
      {status && <div className="sp-alert ok">{status}</div>}

      {/* What the last read actually did. A refused line is named rather than dropped in silence:
          it means the vendor's columns no longer add up to the buying cost printed beside them,
          which is a thing to go and ask about. */}
      {read && (
        <div className={`sp-alert ${read.refused.length ? 'warn' : 'ok'} bl-read`}>
          <b>{t('bench.readOk', { file: read.fileName, n: read.lines })}</b>
          {read.refused.length > 0 && (
            <>
            <span>{t('bench.readRefused', { n: read.refused.length })}</span>
            <ul>
              {read.refused.map((r) => <li key={r}>{r}</li>)}
            </ul>
            </>
          )}
        </div>
      )}

      {/* An empty library is a sentence that says how to fill it. The upload lives on the
          calculator, where somebody has a quote to benchmark; a second upload here would be a
          second way for a sheet to arrive and a second place for the fold to live. */}
      {!busy && !shown.length && (
        <p className="bl-empty">{q ? t('bench.noHit', { q }) : t('bench.empty')}</p>
      )}

      <div className="bl-list">
        {shown.map((s) => (
          <section key={s.id} className={`bl-card${openId === s.id ? ' open' : ''}`}>
            <button type="button" className="bl-cardhead" onClick={() => openSheet(s.id)} aria-expanded={openId === s.id}>
              <div className="bl-name">
                <b>{s.style || s.fileName}</b>
                <span>{s.fileName}</span>
              </div>
              <div className="bl-meta">
                {s.season && <i className="bl-chip">{s.season}</i>}
                {s.vendor && <i className="bl-chip">{s.vendor}</i>}
                <span>{t('bench.lines', { n: s.lineCount })}</span>
                <span>{fmtDate(s.createdAt)}{s.createdBy ? ` · ${s.createdBy}` : ''}</span>
              </div>
            </button>

            {openId === s.id && (
              <div className="bl-body">
                {s.note && <p className="bl-note">{s.note}</p>}
                <div className="bl-actions">
                  <button
                    type="button"
                    className="sp-btn"
                    onClick={() => setEdit({ id: s.id, vendor: s.vendor, season: s.season, note: s.note })}
                  >
                    {t('bench.edit')}
                  </button>
                  <button type="button" className="sp-btn sp-btn-danger" onClick={() => setConfirmDel(s)}>
                    {t('bench.remove')}
                  </button>
                </div>

                {!detail && <p className="bl-empty">{t('bench.loading')}</p>}
                {detail && (
                  <div className="bl-tablewrap">
                    <table className="bl-table">
                      <thead>
                        <tr>
                          <th>{t('bench.colLine')}</th>
                          <th>{t('bench.colVariant')}</th>
                          <th className="num">{t('pricing.skin')}</th>
                          <th className="num">{t('pricing.cmt')}</th>
                          <th className="num">{t('pricing.ohp')}</th>
                          <th className="num">{t('pricing.benchFrameLabel')}</th>
                          {/* The sheet's own figure, last and separated: it is the control the
                              four to its left were checked against, not a fifth cost. */}
                          <th className="num total">{t('bench.colStated')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lines.map((r) => Object.entries(r.variants).map(([k, val], i) => (
                          <tr key={`${r.row}-${r.sku}-${k}`}>
                            {i === 0 ? (
                              <th scope="row" rowSpan={Object.keys(r.variants).length}>
                                <b>{r.style || r.sku}</b>
                                <span>{[r.colorway, r.frameColor, r.sku].filter(Boolean).join(' · ')}</span>
                              </th>
                            ) : null}
                            <td>{val.label}</td>
                            <td className="num">{fmtUSD(val.skin)}</td>
                            <td className="num">{fmtUSD(val.cmt)}</td>
                            <td className="num">{fmtUSD(val.ohp)}</td>
                            <td className="num">{fmtUSD(val.frame)}</td>
                            <td className="num total">{fmtUSD(val.total)}</td>
                          </tr>
                        )))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </section>
        ))}
      </div>

      {edit && (
        <div className="cp-modal-back" onClick={() => setEdit(null)}>
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <h4>{t('bench.editTitle')}</h4>
            {/* The three things the workbook does not state and a person does. The PRICES are not
                editable: they are what the vendor said, and a library that can be edited into
                disagreeing with its own source is worse than no library. */}
            <p>{t('bench.editBody')}</p>
            <label className="cp-field">
              <span>{t('bench.season')}</span>
              <input value={edit.season || ''} onChange={(e) => setEdit({ ...edit, season: e.target.value })} />
            </label>
            <label className="cp-field">
              <span>{t('bench.vendor')}</span>
              <input value={edit.vendor || ''} onChange={(e) => setEdit({ ...edit, vendor: e.target.value })} />
            </label>
            <label className="cp-field">
              <span>{t('bench.note')}</span>
              <input value={edit.note || ''} onChange={(e) => setEdit({ ...edit, note: e.target.value })} />
            </label>
            <div className="cp-modal-actions">
              <button type="button" className="sp-btn" onClick={() => setEdit(null)}>{t('pricing.cancel')}</button>
              <button type="button" className="sp-btn sp-btn-primary" disabled={busy} onClick={saveEdit}>
                {t('bench.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="cp-modal-back" onClick={() => setConfirmDel(null)}>
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <h4>{t('bench.removeTitle')}</h4>
            {/* It says what removing does NOT do, because that is the part somebody would worry
                about: a quote already benchmarked against this sheet keeps its numbers and the
                line printed beside them. */}
            <p>{t('bench.removeBody', { file: confirmDel.fileName })}</p>
            <div className="cp-modal-actions">
              <button type="button" className="sp-btn" autoFocus onClick={() => setConfirmDel(null)}>
                {t('pricing.cancel')}
              </button>
              <button type="button" className="sp-btn sp-btn-danger" disabled={busy} onClick={removeSheet}>
                {t('bench.remove')}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
